/**
 * Escape closes what is in front first (v1's Todos): a detail, then the
 * widget. The chat box's Escape closes the widget in front (26B); while a
 * handler is held here, Escape runs the newest one instead.
 */
import { useEffect, useEffectEvent } from "react";

const stack: (() => void)[] = [];

/** Run the newest handler; false if there is none, and the widget may close. */
export function runEscape(): boolean {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top();
  return true;
}

/** Hold `handler` while the component is mounted. */
export function useEscape(handler: () => void): void {
  const run = useEffectEvent(handler);
  useEffect(() => {
    const entry = () => run();
    stack.push(entry);
    const onKey = (e: KeyboardEvent) => {
      // The chat box asks `runEscape` itself; anywhere else, the window hears it.
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if ((e.target as HTMLElement | null)?.closest?.("textarea[aria-label=Message]")) return;
      if (stack[stack.length - 1] === entry) {
        e.preventDefault();
        entry();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const at = stack.indexOf(entry);
      if (at >= 0) stack.splice(at, 1);
    };
  }, []);
}
